import React from "react";
import { STAGE_LABELS, stageDescription } from "../lib/iris";
import { useUnits, creatinineDisplay, creatinineUnitLabel } from "../context/UnitsContext";
import { AlertCircle, CheckCircle2 } from "lucide-react";

const STAGE_TONE = {
  1: { bg: "bg-success/10", text: "text-[#3f6a52]", ring: "ring-success/30" },
  2: { bg: "bg-warning/10", text: "text-[#8a5a2b]", ring: "ring-warning/30" },
  3: { bg: "bg-accent/10", text: "text-[#8b4a3f]", ring: "ring-accent/30" },
  4: { bg: "bg-destructive/10", text: "text-destructive", ring: "ring-destructive/30" },
};

export default function StageDisplay({ iris }) {
  const { units } = useUnits();

  if (!iris) return null;

  if (!iris.ready) {
    return (
      <div
        className="card-soft p-8 flex items-start gap-4 bg-secondary/50"
        data-testid="stage-not-ready"
      >
        <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex-shrink-0 flex items-center justify-center">
          <AlertCircle size={20} />
        </div>
        <div>
          <h3 className="font-display text-xl">Almost there</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-lg leading-relaxed">
            IRIS staging needs at least two sets of creatinine values from a stable,
            well-hydrated patient. {iris.reason} Once you add the next result, we&apos;ll
            compute the complete stage and management plan.
          </p>
        </div>
      </div>
    );
  }

  const tone = STAGE_TONE[iris.stage];

  return (
    <div className={`card-soft p-8 ${tone.bg}`} data-testid="stage-display">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className={`inline-flex items-center gap-2 text-xs font-medium tracking-wide uppercase ${tone.text}`}>
            <CheckCircle2 size={14} />
            IRIS 2023 Assessment
          </div>
          <h3 className={`font-display text-3xl mt-3 ${tone.text}`} data-testid="stage-label">
            {STAGE_LABELS[iris.stage]}
          </h3>
          <p className="text-sm text-foreground/80 mt-3 max-w-2xl leading-relaxed">
            {stageDescription(iris.stage)}
          </p>
        </div>

        <div className={`rounded-2xl bg-background/70 px-5 py-4 ring-1 ${tone.ring} min-w-[200px]`}>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Stable creatinine</div>
          <div className="font-display text-2xl mt-1" data-testid="stable-creatinine">
            {creatinineDisplay(iris.stableCreatinine, units)}{" "}
            <span className="text-sm text-muted-foreground font-sans">
              {creatinineUnitLabel(units)}
            </span>
          </div>
          {iris.latestSdma != null && (
            <>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-3">SDMA</div>
              <div className="font-display text-xl mt-1">
                {iris.latestSdma} <span className="text-xs text-muted-foreground font-sans">µg/dL</span>
              </div>
              {iris.sdmaBumped && (
                <div className="text-xs text-accent mt-2">
                  SDMA suggests a higher stage than creatinine alone.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div className="rounded-2xl bg-background/70 px-5 py-4">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Proteinuria substage</div>
          {iris.proteinuria ? (
            <div className="mt-1" data-testid="proteinuria-substage">
              <span className="font-display text-lg">
                {iris.proteinuria.code} · {iris.proteinuria.label}
              </span>
              <div className="text-xs text-muted-foreground mt-1">UPC = {iris.proteinuria.upc}</div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground mt-1">Add a UPC to substage.</div>
          )}
        </div>

        <div className="rounded-2xl bg-background/70 px-5 py-4">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Blood pressure substage</div>
          {iris.bp ? (
            <div className="mt-1" data-testid="bp-substage">
              <span className="font-display text-lg">
                {iris.bp.code} · {iris.bp.label}
              </span>
              <div className="text-xs text-muted-foreground mt-1">
                SBP {iris.bp.sbp} mmHg · {iris.bp.risk}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground mt-1">Add a systolic BP to substage.</div>
          )}
        </div>
      </div>
    </div>
  );
}
